import { Button, Form, Input, message, Select, Upload } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import settingsApi from '@/api/settings';
import categoriesApi from '@/api/categories';
import './AddCategorie.scss';

const MAX_QUESTIONS_COUNT = 10;

const isPersistedQuestionId = (id: unknown) => {
    if (id == null) return false;
    return /^\d+$/.test(String(id));
};

const buildUpdateQuestionPayload = (questions: any[]) => {
    return questions
        .map(q => ({
            questionId: q.isAdd || !isPersistedQuestionId(q.questionId) ? null : q.questionId,
            question: q.question?.trim() ?? '',
            deleted: !!q.deleted,
        }))
        .filter(q => q.deleted || q.question);
};

interface editCategoryType {
    edit: boolean;
    categoryId: string;
    categoryName: string;
    categoryIcon: string;
    categoryPosition: number;
    categoryDesc: string;
    questionList: any[];
}

const AddCategorie = () => {
    const category = useSelector((state: any) => state.state.category);
    const [editCategory, setEditCategory] = useState<editCategoryType>({
        edit: false,
        categoryId: '',
        categoryName: '',
        categoryIcon: '',
        categoryPosition: 0,
        categoryDesc: '',
        questionList: [],
    });
    const link = useNavigate();
    const [form] = Form.useForm();
    const formValues = Form.useWatch([], form);

    const [questions, setQuestions] = useState<any[]>([]);

    const renderQuestion = useMemo(() => {
        return questions.filter(q => !q?.deleted);
    }, [questions]);

    const onFinish = async (values: any) => {
        const { categoryName, categoryPosition, categoryDesc } = values;
        const { categoryIcon } = editCategory;

        const params = {
            categoryName,
            categoryPosition,
            categoryDesc,
            questionList: [{}],
            categoryIcon,
        };

        if (editCategory.edit) {
            params.questionList = buildUpdateQuestionPayload(questions);
            try {
                await categoriesApi.updateCategory(editCategory.categoryId, params);
                link(-1);
            } catch (error: any) {
                message.error(error);
            }
        } else {
            params.questionList = questions.map(q => q.question?.trim()).filter(Boolean);
            try {
                await categoriesApi.addCategory(params);
                link(-1);
            } catch (error: any) {
                message.error(error);
            }
        }
    };

    const onFinishFailed = (errorInfo: any) => {
        message.error(errorInfo.errorFields[0].errors[0]);
    };

    const [uploading, setUploading] = useState(false);
    const upload = async (file: File) => {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            const res = await settingsApi.uploadFile(formData);
            setEditCategory(state => ({ ...state, categoryIcon: res.data }));
            form.validateFields(['categoryIcon']).catch(() => undefined);
        } catch (error: any) {
            message.error(error);
        } finally {
            setUploading(false);
        }
    };

    const beforeUpload = (file: File) => {
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            message.error('Image must smaller than 5MB!');
            return false;
        }
        upload(file);
        return false;
    };

    const [positions, setPositions] = useState<{ value: number; label: number; disabled: boolean }[]>([]);
    const getMaxPosition = async (isEdit: boolean) => {
        try {
            const res = await categoriesApi.maxPosition();
            const max = isEdit ? res.data : res.data + 1;
            const options: { value: number; label: number; disabled: boolean }[] = [];
            for (let index = 1; index < max; index++) {
                options.push({ value: index, label: index, disabled: false });
            }
            setPositions(options);
        } catch (error: any) {
            message.error(error);
        }
    };

    useEffect(() => {
        if (category.edit) {
            const list = category.questionList.map((q, index) => {
                if (typeof q === 'string') {
                    return { clientKey: `draft-${index}`, questionId: null, question: q, deleted: false, isAdd: true };
                }
                const persisted = isPersistedQuestionId(q.questionId);
                return {
                    ...q,
                    clientKey: persisted ? String(q.questionId) : `draft-${index}`,
                    questionId: persisted ? q.questionId : null,
                    question: q.question || '',
                    deleted: false,
                    isAdd: !persisted,
                };
            });
            setEditCategory({ ...category });
            setQuestions(list);
            form.setFieldsValue(category);
        }
        void getMaxPosition(!!category.edit);
    }, []);

    const onCancel = () => {
        form.resetFields();
        link(-1);
    };

    const handleQuestionInput = (e, clientKey) => {
        setQuestions(state => state.map(q => (q.clientKey === clientKey ? { ...q, question: e.target.value } : q)));
    };

    const handleQuestionDelete = clientKey => {
        setQuestions(state => state.map(q => (q.clientKey === clientKey ? { ...q, deleted: true } : q)));
    };

    const handleQuestionAdd = () => {
        if (renderQuestion.length >= MAX_QUESTIONS_COUNT) return;
        setQuestions(state => [
            ...state,
            { clientKey: `draft-${Date.now()}`, questionId: null, question: '', deleted: false, isAdd: true },
        ]);
    };

    const validateIcon = () => {
        if (editCategory.categoryIcon) {
            return Promise.resolve();
        }
        return Promise.reject(new Error('Please upload an icon'));
    };

    const isSaveDisabled =
        !editCategory.categoryIcon ||
        !formValues?.categoryName ||
        !formValues?.categoryPosition ||
        uploading ||
        !formValues?.categoryDesc;

    return (
        <div className="page-main add-Categorie">
            <div className="add-category-header">
                <div className="page-title">
                    <img
                        className="back-icon"
                        src={require('@/assets/images/back-icon.png')}
                        alt="back"
                        onClick={() => link(-1)}
                    />
                    {category.edit ? 'Edit category' : 'Add new category'}
                </div>
                <p className="add-category-subtitle">Create or update category details</p>
            </div>

            <div className="add-category-card">
                <Form
                    className="add-Categorie-form"
                    form={form}
                    layout="vertical"
                    initialValues={{ remember: true }}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    validateTrigger="onBlur"
                    autoComplete="off"
                >
                    <div className="form-layout">
                        <div className="form-column">
                            <section className="form-section">
                                <h3 className="form-section-title">Basic details</h3>
                                <div className="form-section-grid">
                                    <Form.Item
                                        label="Name"
                                        name="categoryName"
                                        rules={[{ required: true, message: 'Please enter name' }]}
                                    >
                                        <Input className="default_input" maxLength={50} placeholder="Category name" />
                                    </Form.Item>
                                    <Form.Item
                                        label="Position"
                                        name="categoryPosition"
                                        rules={[{ required: true, message: 'Please select position' }]}
                                    >
                                        <Select
                                            className="form-select"
                                            placeholder="Select position"
                                            suffixIcon={
                                                <img
                                                    src={require('@/assets/images/select-icon.png')}
                                                    alt=""
                                                    style={{ width: 24, height: 24 }}
                                                />
                                            }
                                            options={positions}
                                        />
                                    </Form.Item>
                                </div>
                            </section>

                            <section className="form-section">
                                <h3 className="form-section-title">Category icon</h3>
                                <Form.Item
                                    name="categoryIcon"
                                    rules={[{ validator: validateIcon }]}
                                    className="form-item-icon"
                                >
                                    <div className="upload-img">
                                        {editCategory.categoryIcon ? (
                                            <img className="uploaded-photo" src={editCategory.categoryIcon} alt="" />
                                        ) : (
                                            <div className="upload-photo">
                                                <img src={require('@/assets/images/upload-img.png')} alt="" />
                                            </div>
                                        )}
                                        <div className="upload-img-actions">
                                            <Upload
                                                accept="image/*"
                                                maxCount={1}
                                                beforeUpload={beforeUpload}
                                                className="category-upload-image"
                                                showUploadList={false}
                                            >
                                                {editCategory.categoryIcon ? (
                                                    <img
                                                        className="re-upload"
                                                        src={require('@/assets/images/re-upload.png')}
                                                        alt="re-upload"
                                                    />
                                                ) : null}
                                                <span className="upload-tip">Click to upload</span>
                                            </Upload>
                                            <div className="max-upload-tip">Max. file size 5MB</div>
                                        </div>
                                    </div>
                                </Form.Item>
                            </section>
                        </div>

                        <div className="form-column">
                            <section className="form-section">
                                <h3 className="form-section-title">Questions</h3>
                                <p className="form-section-hint">
                                    Questions that prompt a Yes or No answer relative to this category
                                </p>
                                <Form.Item className="form-item-questions">
                                    <div className="questions">
                                        {renderQuestion?.map((q, index) => (
                                            <div key={q.clientKey ?? index} className="question-items">
                                                <div className="question-items__text">No.{index + 1}</div>
                                                <Input
                                                    className="question-items__input"
                                                    placeholder="eg. I have my own TV Bracket"
                                                    maxLength={100}
                                                    value={q.question}
                                                    onChange={e => handleQuestionInput(e, q.clientKey)}
                                                />
                                                <img
                                                    className="question-items__icon"
                                                    src={require('@/assets/images/delete-icon.png')}
                                                    alt="delete"
                                                    onClick={() => handleQuestionDelete(q.clientKey)}
                                                />
                                            </div>
                                        ))}
                                        {renderQuestion.length < MAX_QUESTIONS_COUNT && (
                                            <button type="button" className="add-question" onClick={handleQuestionAdd}>
                                                <span>+</span>
                                                <span>Add question</span>
                                            </button>
                                        )}
                                    </div>
                                </Form.Item>
                            </section>

                            <section className="form-section">
                                <h3 className="form-section-title">Notes</h3>
                                <Form.Item label="Category notes" name="categoryDesc">
                                    <Input.TextArea
                                        className="category-notes"
                                        maxLength={500}
                                        showCount
                                        autoSize={{ minRows: 4, maxRows: 8 }}
                                        placeholder="Add notes for this category"
                                    />
                                </Form.Item>
                            </section>
                        </div>
                    </div>

                    <div className="form-actions">
                        <Button className="cancel-btn" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button type="primary" htmlType="submit" loading={uploading} disabled={isSaveDisabled}>
                            Save
                        </Button>
                    </div>
                </Form>
            </div>
        </div>
    );
};

export default AddCategorie;
